import axios from 'axios'
import { DeactivateCreditCardWriteResponse } from 'src/queries/creditCards/deactivateCreditCard/deactivateCreditCard.models'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import { BackendErrorPayload } from 'src/utils/error.utils'

/**
 * Pure network fn for deactivating a credit-card-backed budget
 * (`PUT /creditcards/:id/deactivate`). Replaces the
 * `deactivateCreditCardAndCloseModal` thunk's axios call, which used
 * `selectAuthedJsonApiRequestConfig` — `RequestConfig.Default` here.
 */
export const deactivateCreditCard = async (
  creditCardId: string,
): Promise<DeactivateCreditCardWriteResponse> =>
  getResponseData(
    axios.put<DeactivateCreditCardWriteResponse>(
      `/creditcards/${creditCardId}/deactivate`,
      {},
      getRequestConfig(RequestConfig.Default),
    ),
  )

/**
 * Recover the raw backend error payload a mutation failure carries.
 * `normalizeQueryError` (network.utils.ts) stashes the original AxiosError on
 * the non-enumerable `QueryError.originalError`; the `deactivateBudget`
 * flyout's `DEACTIVATE_CREDIT_CARD_FAILURE` case only tracks a loading flag
 * today, but this keeps the recovered shape consistent with the other
 * creditCards write mutations in case a future consumer parses `details`.
 */
export const recoverBackendErrorPayload = (
  errorSource: unknown,
): BackendErrorPayload<string> => {
  if (axios.isAxiosError(errorSource) && errorSource.response?.data) {
    return errorSource.response.data
  }
  if (
    errorSource !== null &&
    typeof errorSource === 'object' &&
    'error' in errorSource
  ) {
    return errorSource as BackendErrorPayload<string>
  }
  return { error: 'Sorry, something went wrong. Please try again later.' }
}
